/**
 * Inference narratives — ~80 non-repeating messages shown while the reader
 * waits for the first token.
 *
 * Voice: the AI narrating its own work, present participle, itself the
 * implied subject — "Using layer normalization…", not "Layer normalization
 * keeps activations stable" (Brian, UAT 2026-09-21). The reader is watching
 * something work, not reading a textbook at it.
 *
 * Categories: what's happening, hardware, how LLMs work, corpus, privacy,
 * and Scripture. Hardware lines are templated from the inference-meta SSE
 * event.
 */

export interface NarrativeMeta {
  hostname?: string;
  cpuModel?: string;
  cpuCores?: number;
  totalMemoryGb?: number;
  freeMemoryGb?: number;
  platform?: string;
  arch?: string;
  ollamaHost?: string;
  model?: string;
}

type NarrativeEntry = string | ((meta: NarrativeMeta) => string);

const NARRATIVES: NarrativeEntry[] = [
  // --- What's happening now (~15) ---
  'Scanning the full context window with attention layers',
  'Computing token probabilities through softmax',
  'Generating autoregressively — conditioning each token on every one before it',
  'Sampling with temperature for controlled variation in word choice',
  'Reusing the KV-cache instead of recomputing prior attention states',
  'Attending to several parts of your question at once, across multiple heads',
  'Using layer normalization to keep activations stable through 32+ layers',
  'Grounding every answer in Scripture and the commentary corpus',
  'Comparing embedding vectors against 452,000+ indexed chunks',
  'Decoding one token at a time — nothing here generates in parallel',
  'Passing each attention output through feed-forward layers',
  'Reading word order out of the positional encodings in your question',
  'Projecting hidden states into vocabulary probabilities',
  'Taking the highest-probability token at each step',
  'Carrying signal through 32 transformer blocks on residual connections',

  // --- Hardware (~12, templated) ---
  (m) => `Running on ${m.hostname ?? 'the server'} — keeping every computation local`,
  (m) => m.cpuModel
    ? `Working on ${m.cpuModel} (${m.cpuCores ?? '?'} cores)`
    : 'Working on dedicated hardware',
  (m) => m.totalMemoryGb
    ? `Drawing on ${m.totalMemoryGb} GB of system RAM — ${m.freeMemoryGb ?? '?'} GB still free`
    : 'Watching memory pressure as I work',
  (m) => `Running on ${m.platform ?? 'linux'} ${m.arch ?? 'x64'}`,
  (m) => `Thinking with ${m.model ?? 'llama3.2'} through Ollama`,
  (m) => `Answering from ${m.ollamaHost ?? 'http://localhost:11434'}`,
  (m) => m.cpuCores
    ? `Spreading the work across ${m.cpuCores} CPU threads`
    : 'Spreading the work across every available core',
  (m) => `Inferring on ${m.hostname ?? 'this local server'} — depending on no cloud at all`,
  (m) => m.totalMemoryGb
    ? `Holding ~4 GB of weights inside ${m.totalMemoryGb} GB of RAM`
    : 'Holding the model weights in RAM',
  (m) => m.cpuModel?.includes('Intel')
    ? 'Accelerating matrix math with AVX2 on Intel silicon'
    : m.cpuModel?.includes('AMD')
      ? 'Accelerating matrix math with AVX2 on AMD silicon'
      : 'Accelerating matrix multiplication with SIMD instructions',
  (m) => `Doing all the matrix math on ${m.cpuModel ?? 'the CPU'} — no GPU in this box`,
  (m) => m.freeMemoryGb
    ? `Keeping ${m.freeMemoryGb} GB of RAM clear for the KV-cache and context`
    : 'Keeping RAM headroom clear for the KV-cache',

  // --- How I work (~15) ---
  'Splitting words into subword tokens — "unrighteous" becomes "un", "right", "eous"',
  'Attending rather than recurring — the transformer design published in 2017',
  'Mapping each token into a 2048+ dimensional vector space',
  'Reading 4-bit quantized weights (Q4_K_M) — near 16-bit quality at a quarter the size',
  'Loading weights from GGUF, a format tuned for inference without a GPU',
  'Drawing on pre-training over trillions of tokens, then fine-tuning on the Bible corpus',
  'Retrieving source text before generating — grounded, not recalled',
  'Holding your question, the system prompt, and retrieved Scripture in context together',
  'Trimming the unlikely tail with top-p before I sample',
  'Tracking perplexity as I go — lower means I am more sure',
  'Weighing several candidate continuations before committing to one',
  'Building vocabulary out of character-pair frequencies (byte pair encoding)',
  'Working down through the layers — the deeper ones carry the more abstract patterns',
  'Computing Query, Key, and Value matrices in every layer',
  'Reading frozen weights — inference never writes back to the model',

  // --- The corpus (~15) ---
  'Searching 31,102 KJV verses across 66 books',
  "Tracing English words back to their Hebrew and Greek roots through Strong's",
  "Consulting 14,000+ Strong's entries across the Hebrew and Greek lexicons",
  'Following 345,000+ cross-references between the Testaments',
  'Drawing on 40,000+ commentary passages from Halley, McGee, and Smith',
  'Searching 452,000+ embedding vectors in ChromaDB',
  "Comparing the KJV's Textus Receptus against the modern critical texts",
  'Checking 23,000+ variant entries for manuscript differences',
  "Looking up archaic KJV vocabulary in the Jones Dictionary's 514 entries",
  'Embedding each chunk as a 768-dimensional vector with nomic-embed-text',
  "Reading six collections: verses, Strong's, cross-refs, variants, Jones, commentary",
  'Verifying every verse I retrieve against the SQLite KJV database',
  'Weighing three commentators, spanning the 1920s to the 1980s',
  'Ranking cross-references by community votes — the strongest links first',
  'Matching meaning rather than keywords',

  // --- Privacy (~10) ---
  'Running locally — your questions never leave this network',
  'Answering without a cloud API key — the whole stack is self-hosted',
  'Collecting no telemetry, no analytics, no tracking cookies',
  'Leaving your conversation history on this device',
  'Running in an isolated container — needing no internet access',
  'Standing on open source: Ollama, ChromaDB, Fastify, React',
  'Generating every token here — handing nothing to a third-party provider',
  'Talking to Ollama over localhost — nothing crosses the wire',
  'Training on nothing you type — your data stays yours',
  'Degrading gracefully — cached data keeps this working offline',

  // --- Scripture (~10) ---
  'Reading the KJV of 1611 — 47 scholars, 7 years of work',
  'Counting Psalm 119 the longest chapter — 176 verses',
  'Counting Psalm 117 the shortest — just 2 verses',
  'Marking the middle verse of the Bible at Psalm 118:8',
  'Noting that Esther and Song of Solomon never name God',
  'Measuring the longest verse, Esther 8:9 — 90 words',
  'Measuring the shortest verse, John 11:35 — "Jesus wept."',
  'Counting "the LORD" 6,510 times in the Old Testament',
  'Counting "and" 46,277 times across the KJV',
  'Working across 8,674 unique Hebrew words and 5,624 unique Greek',
];

/** Get a narrative message by index. Never repeats until all are exhausted. */
export function getNarrative(index: number, meta?: NarrativeMeta): string {
  const entry = NARRATIVES[index % NARRATIVES.length];
  if (typeof entry === 'function') return entry(meta ?? {});
  return entry;
}

export const NARRATIVE_COUNT = NARRATIVES.length;
